import { useState, useEffect, useRef } from 'react';
import {
  MdDelete, MdDownload, MdUpload,
  MdDescription, MdClose, MdCalendarMonth, MdCheckCircle,
  MdPending, MdThumbUp, MdThumbDown,
} from 'react-icons/md';
import ActionMenu from '../components/common/ActionMenu';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import { format } from 'date-fns';

const API_URL =
  process.env.NODE_ENV === 'production'
    ? (process.env.REACT_APP_API_URL || window.location.origin)
    : 'http://localhost:5000';

const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const STATUS_CONFIG = {
  pending:  { icon: <MdPending  size={13}/>, cls: 'badge-review',      label:'Pending'  },
  reviewed: { icon: <MdCheckCircle size={13}/>, cls: 'badge-in-progress', label:'Reviewed' },
  approved: { icon: <MdThumbUp  size={13}/>, cls: 'badge-done',        label:'Approved' },
  rejected: { icon: <MdThumbDown size={13}/>, cls: 'badge-critical',   label:'Rejected' },
};

const fmtSize = (bytes) => {
  if (!bytes) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024*1024) return `${(bytes/1024).toFixed(1)} KB`;
  return `${(bytes/(1024*1024)).toFixed(1)} MB`;
};

// ─── Upload Modal ─────────────────────────────────────────────
const UploadModal = ({ onClose, onSaved }) => {
  const [file, setFile]   = useState(null);
  const [month, setMonth] = useState(String(new Date().getMonth()+1));
  const [year, setYear]   = useState(String(new Date().getFullYear()));
  const [note, setNote]   = useState('');
  const [loading, setLoading] = useState(false);
  const [drag, setDrag]   = useState(false);
  const fileRef = useRef();

  const years = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i);

  const handleDrop = (e) => {
    e.preventDefault(); setDrag(false);
    const f = e.dataTransfer.files[0];
    if (f) setFile(f);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file) { toast.error('Please select a file'); return; }
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('month', month);
      fd.append('year', year);
      fd.append('note', note);
      await api.post('/enrollments', fd, { headers: { 'Content-Type':'multipart/form-data' } });
      toast.success('Sheet uploaded successfully!');
      onSaved(); onClose();
    } catch (err) { toast.error(err.response?.data?.message || 'Upload failed'); }
    finally { setLoading(false); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 480 }} onClick={(e)=>e.stopPropagation()}>
        <div className="modal-header">
          <h3>Upload Enrollment Sheet</h3>
          <button className="btn-icon" onClick={onClose}><MdClose /></button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            {/* Drop zone */}
            <div
              onDragOver={(e)=>{e.preventDefault();setDrag(true);}}
              onDragLeave={()=>setDrag(false)}
              onDrop={handleDrop}
              onClick={() => fileRef.current.click()}
              style={{
                border: `2px dashed ${drag?'var(--primary)':'var(--border2)'}`,
                borderRadius: 12, padding:'28px 20px', textAlign:'center',
                cursor:'pointer', marginBottom: 18,
                background: drag ? 'var(--primary-light)' : 'var(--surface2)',
                transition:'all .15s',
              }}
            >
              <input ref={fileRef} type="file" accept=".xls,.xlsx,.csv,.pdf" style={{ display:'none' }} onChange={(e)=>setFile(e.target.files[0])} />
              {file ? (
                <div style={{ display:'flex', alignItems:'center', justifyContent:'center', gap: 10 }}>
                  <MdDescription size={28} color="var(--primary)" />
                  <div style={{ textAlign:'left' }}>
                    <div style={{ fontWeight:700, fontSize:'0.875rem' }}>{file.name}</div>
                    <div className="text-muted" style={{ fontSize:'0.75rem' }}>{fmtSize(file.size)}</div>
                  </div>
                  <button type="button" onClick={(e)=>{e.stopPropagation();setFile(null);}} style={{ marginLeft:'auto', background:'none', border:'none', color:'var(--danger)', cursor:'pointer' }}><MdClose /></button>
                </div>
              ) : (
                <>
                  <MdUpload size={36} color="var(--text-muted)" />
                  <div style={{ fontWeight:600, marginTop: 8, fontSize:'0.875rem' }}>Drag & drop or click to browse</div>
                  <div className="text-muted" style={{ fontSize:'0.75rem', marginTop: 4 }}>Supports XLS, XLSX, CSV, PDF · Max 10MB</div>
                </>
              )}
            </div>

            <div className="grid grid-2">
              <div className="form-group">
                <label className="form-label">Month *</label>
                <select className="form-control" value={month} onChange={(e)=>setMonth(e.target.value)}>
                  {MONTHS.map((m,i) => <option key={m} value={String(i+1)}>{m}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Year *</label>
                <select className="form-control" value={year} onChange={(e)=>setYear(e.target.value)}>
                  {years.map((y) => <option key={y} value={String(y)}>{y}</option>)}
                </select>
              </div>
            </div>
            <div className="form-group" style={{ marginBottom:0 }}>
              <label className="form-label">Note (optional)</label>
              <textarea className="form-control" rows={2} value={note} onChange={(e)=>setNote(e.target.value)} placeholder="Any additional notes..." />
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={loading || !file}>
              {loading ? 'Uploading...' : <><MdUpload /> Upload Sheet</>}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ─── Main Page ────────────────────────────────────────────────
const Enrollments = () => {
  useAuth();
  const [sheets, setSheets]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUpload, setShowUpload] = useState(false);
  const [, setDownloading] = useState(null);

  const fetchSheets = async () => {
    const { data } = await api.get('/enrollments/my');
    setSheets(data.data); setLoading(false);
  };
  useEffect(() => { fetchSheets(); }, []);

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this sheet?')) return;
    await api.delete(`/enrollments/${id}`);
    toast.success('Sheet deleted'); fetchSheets();
  };

  const handleDownload = async (sheet) => {
    setDownloading(sheet._id);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/enrollments/${sheet._id}/download`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) { const err = await res.json(); throw new Error(err.message || 'Download failed'); }
      const blob = await res.blob();
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href = url; a.download = sheet.originalName; a.click();
      URL.revokeObjectURL(url);
      toast.success('Downloaded!');
    } catch (err) { toast.error(err.message || 'Download failed'); }
    finally { setDownloading(null); }
  };

  const statusCfg = (s) => STATUS_CONFIG[s] || STATUS_CONFIG.pending;

  if (loading) return <div className="loading"><div className="spinner"/></div>;

  return (
    <main className="page">
      {showUpload && <UploadModal onClose={() => setShowUpload(false)} onSaved={fetchSheets} />}

      <div className="page-header">
        <div>
          <h1 className="page-title">Enrollment Sheets</h1>
          <p className="page-subtitle">{sheets.length} sheet{sheets.length!==1?'s':''} submitted</p>
        </div>
        <button className="btn btn-primary" onClick={() => setShowUpload(true)}><MdUpload /> Upload Sheet</button>
      </div>

      {sheets.length === 0 ? (
        <div className="empty-state">
          <MdDescription size={56}/>
          <h3>No sheets uploaded yet</h3>
          <p>Upload your monthly enrollment sheets to get started.</p>
          <button className="btn btn-primary" style={{ marginTop:16 }} onClick={() => setShowUpload(true)}><MdUpload /> Upload First Sheet</button>
        </div>
      ) : (
        <div className="card">
          <div className="table-wrapper">
            <table>
              <thead>
                <tr><th>Period</th><th>File</th><th>Size</th><th>Note</th><th>Status</th><th>Admin Note</th><th>Submitted</th><th>Actions</th></tr>
              </thead>
              <tbody>
                {sheets.map((s) => {
                  const sc = statusCfg(s.status);
                  return (
                    <tr key={s._id}>
                      <td>
                        <div style={{ fontWeight:700, fontSize:'0.875rem' }}>{MONTHS[s.month-1]} {s.year}</div>
                        <div style={{ display:'flex', alignItems:'center', gap:4 }}>
                          <MdCalendarMonth size={12} color="var(--text-muted)" />
                          <span className="text-muted" style={{ fontSize:'0.72rem' }}>Month {s.month}</span>
                        </div>
                      </td>
                      <td>
                        <div style={{ display:'flex', alignItems:'center', gap: 8 }}>
                          <div style={{ width:32, height:32, borderRadius:8, background:'var(--primary-light)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                            <MdDescription size={16} color="var(--primary)"/>
                          </div>
                          <div>
                            <div style={{ fontWeight:600, fontSize:'0.82rem', maxWidth:180, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{s.originalName}</div>
                            <div className="text-muted" style={{ fontSize:'0.7rem', textTransform:'uppercase' }}>{s.originalName?.split('.').pop()}</div>
                          </div>
                        </div>
                      </td>
                      <td className="text-muted" style={{ fontSize:'0.82rem' }}>{fmtSize(s.fileSize)}</td>
                      <td className="text-muted" style={{ fontSize:'0.82rem', maxWidth:140 }}>{s.note||'—'}</td>
                      <td>
                        <span className={`badge ${sc.cls}`} style={{ gap:4 }}>{sc.icon}{sc.label}</span>
                      </td>
                      <td className="text-muted" style={{ fontSize:'0.82rem', maxWidth:160 }}>{s.adminNote||'—'}</td>
                      <td className="text-muted" style={{ fontSize:'0.78rem', whiteSpace:'nowrap' }}>
                        {format(new Date(s.createdAt),'MMM d, yyyy')}
                      </td>
                      <td>
                        <div style={{ display:'flex', gap:4 }}>
                          <ActionMenu items={[
                            { label: 'Download', icon: <MdDownload size={15}/>, onClick: () => handleDownload(s) },
                            'divider',
                            { label: 'Delete',   icon: <MdDelete size={15}/>,   onClick: () => handleDelete(s._id), danger: true },
                          ]}/>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </main>
  );
};

export default Enrollments;
